import { useEffect, useRef, useState, useCallback } from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

interface ResponsiveItemListProps<T> {
  items: T[]
  renderItem: (item: T, isVisible: boolean) => React.ReactNode
  getKey: (item: T) => string
  moreButtonWidth?: number
  containerClassName?: string
  dropdownClassName?: string
  dropdownItemClassName?: string
  moreButtonContent?: (count: number) => React.ReactNode
}

export function ResponsiveItemList<T>({
  items,
  renderItem,
  getKey,
  moreButtonWidth = 180,
  containerClassName,
  dropdownClassName,
  dropdownItemClassName,
  moreButtonContent = count => `+${count} more`,
}: ResponsiveItemListProps<T>) {
  const [showDropdown, setShowDropdown] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleItems, setVisibleItems] = useState<T[]>(items)
  const [hiddenItems, setHiddenItems] = useState<T[]>([])
  const [isMeasuring, setIsMeasuring] = useState(true)
  const [isCalculating, setIsCalculating] = useState(false)
  const rafRef = useRef<number>()
  const resizeTimeoutRef = useRef<NodeJS.Timeout>()

  // Use refs to store current values without causing re-renders
  const itemsRef = useRef(items)
  const moreButtonWidthRef = useRef(moreButtonWidth)

  // Update refs when props change
  itemsRef.current = items
  moreButtonWidthRef.current = moreButtonWidth

  // Stable calculation function that doesn't depend on props
  const calculateVisibleItems = useCallback(() => {
    const container = containerRef.current
    const currentItems = itemsRef.current
    const currentMoreButtonWidth = moreButtonWidthRef.current

    if (!container || !currentItems.length) {
      setVisibleItems([])
      setHiddenItems([])
      setIsMeasuring(false)
      setIsCalculating(false)
      return
    }

    const containerRect = container.getBoundingClientRect()
    const containerWidth = containerRect.width

    // Early return if container has no width
    if (containerWidth === 0) {
      setIsMeasuring(false)
      setIsCalculating(false)
      return
    }

    // Set measuring state only when we need to temporarily render all items
    const needsRemeasurement =
      visibleItems.length !== currentItems.length ||
      hiddenItems.length > 0 ||
      visibleItems.length === 0

    if (needsRemeasurement) {
      setIsMeasuring(true)
      setIsCalculating(true)
      // Temporarily render all items to measure them
      setVisibleItems(currentItems)
      setHiddenItems([])
    }

    // Cancel previous RAF if exists
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
    }

    // Wait for render
    rafRef.current = requestAnimationFrame(() => {
      const itemElements = Array.from(
        container.getElementsByClassName("responsive-item"),
      ) as HTMLElement[]

      if (itemElements.length === 0) {
        setIsMeasuring(false)
        setIsCalculating(false)
        return
      }

      // Get actual positions and widths
      const itemData = itemElements.map((element, index) => {
        const rect = element.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        return {
          index,
          item: currentItems[index],
          width: rect.width,
          left: rect.left - containerRect.left,
          right: rect.right - containerRect.left,
          element,
        }
      })

      // Find where items actually wrap by checking positions
      let lastItemInFirstRow = itemData.length - 1

      if (itemData.length > 1) {
        const firstRowTop = itemData[0].element.getBoundingClientRect().top

        for (let i = 1; i < itemData.length; i++) {
          const currentTop = itemData[i].element.getBoundingClientRect().top
          const topDifference = Math.abs(currentTop - firstRowTop)

          if (topDifference > 10) {
            // Item wrapped to next row
            lastItemInFirstRow = i - 1
            break
          }
        }
      }

      // Check if we need more button
      const itemsInFirstRow = lastItemInFirstRow + 1
      const hasOverflow = itemsInFirstRow < currentItems.length

      if (hasOverflow) {
        // We have overflow, check if more button fits
        const lastVisibleItem = itemData[lastItemInFirstRow]
        const spaceUsed = lastVisibleItem.right
        const spaceRemaining = containerWidth - spaceUsed

        if (spaceRemaining >= currentMoreButtonWidth) {
          // More button fits, show all first row items
          const visible = currentItems.slice(0, itemsInFirstRow)
          const hidden = currentItems.slice(itemsInFirstRow)

          setVisibleItems(visible)
          setHiddenItems(hidden)
        } else {
          // More button doesn't fit, hide one more item
          const visible = currentItems.slice(
            0,
            Math.max(0, itemsInFirstRow - 1),
          )
          const hidden = currentItems.slice(visible.length)

          setVisibleItems(visible)
          setHiddenItems(hidden)
        }
      } else {
        // No overflow, show all items
        setVisibleItems(currentItems)
        setHiddenItems([])
      }

      setIsMeasuring(false)
      setIsCalculating(false)
    })
  }, [visibleItems.length, hiddenItems.length]) // Add dependencies to check if remeasurement is needed

  // Stable resize handler
  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current)
    }

    resizeTimeoutRef.current = setTimeout(() => {
      setIsCalculating(true)
      calculateVisibleItems()
    }, 100)
  }, [calculateVisibleItems])

  // Effect that only runs when items change
  useEffect(() => {
    if (!containerRef.current || !items.length) {
      setVisibleItems([])
      setHiddenItems([])
      setIsMeasuring(false)
      setIsCalculating(false)
      return
    }

    // Only trigger measuring if we actually need to recalculate
    // Don't set measuring state immediately to avoid flashing
    calculateVisibleItems()
  }, [items, calculateVisibleItems])

  // Separate effect for resize listener (only runs once)
  useEffect(() => {
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
      // Clean up RAF on unmount
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      // Clean up resize timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current)
      }
    }
  }, [handleResize])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <div
      className={cn("flex flex-wrap min-w-full w-full", containerClassName)}
      ref={containerRef}
      style={{
        opacity: isCalculating ? 0 : 1,
        transition: "opacity 0.15s ease-in-out",
      }}
    >
      {!isMeasuring && (
        <>
          {visibleItems.map(item => (
            <div key={getKey(item)} className="responsive-item">
              {renderItem(item, true)}
            </div>
          ))}
          {hiddenItems.length > 0 && (
            <div className="relative">
              <div
                className={cn(
                  "responsive-item capitalize rounded-system-xl p-system-xs cursor-pointer leading-none bg-system-blue-700",
                  dropdownItemClassName,
                )}
                onClick={() => setShowDropdown(!showDropdown)}
              >
                {moreButtonContent(hiddenItems.length)}
              </div>
              {showDropdown && (
                <div
                  className={cn(
                    "absolute top-full right-0 mt-system-xxs bg-system-blue-800 rounded-system-md shadow-lg p-system-xxs z-[99999] w-[300px] border-2 border-system-blue-700",
                    dropdownClassName,
                  )}
                >
                  {hiddenItems.map(item => (
                    <div key={getKey(item)} className="responsive-item">
                      {renderItem(item, false)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {isMeasuring && (
        <>
          {items.map(item => (
            <div key={getKey(item)} className="responsive-item">
              {renderItem(item, true)}
            </div>
          ))}
        </>
      )}
    </div>
  )
}
